import React, { Fragment } from "react";
import { Grid, Typography, Button } from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../reducers";
import {
  thunkPushDownload,
  thunkAddSubtitle,
  thunkDelSubtitle,
  thunkRenameSubtitle,
  thunkUpdateTorrent,
  thunkPauseTorrents,
  thunkResumeTorrents
} from "../../thunks";
import { Torrent, TorrentStatus } from "../../types";
import Editable from "../Shared/Editable";
import FileButton from "../Shared/FileButton";
import client from "../../thunks/client";
import { toggleTorrentPending, setToastMsg } from "../../actions";

export default function General(props: {
  classes: {
    tabContent: string;
    title: string;
    rowLabel: string;
    error: string;
    grow: string;
    buttons: string;
  };
}) {
  const { classes } = props;
  const dispatch = useDispatch();
  const active = useSelector(
    (state: RootState) => state.torrent.active
  ) as Torrent;

  const labelWidth: any = 3;
  const contentWidth: any = 12 - labelWidth;

  const pushDownload = () => dispatch(thunkPushDownload(active.id));

  const handleAddSubtitle = (path: string, base64?: string) => {
    if (!path || !base64 || !active || !active.id) {
      return;
    }
    dispatch(thunkAddSubtitle(active.id, path, base64));
  };

  const handleDelSubtitle = (subtitle: string) => {
    dispatch(thunkDelSubtitle(active.id, subtitle));
  };

  const handleRenameSubtitle = (oldName: string, newName: string) => {
    dispatch(thunkRenameSubtitle(active.id, oldName, newName));
  };

  const handleUpdateTorrent = (data: {
    title?: string;
    season?: number | null;
    episode?: number | null;
  }) => {
    dispatch(thunkUpdateTorrent(active.id, data));
  };

  const handlePause = () => {
    dispatch(thunkPauseTorrents([active.id], [active.hash]));
  }

  const handleResume = () => {
    dispatch(thunkResumeTorrents([active.id], [active.hash]));
  }

  const handleExport = async () => {
    dispatch(toggleTorrentPending(active.id, true));
    try {
      const res = await client.get(`/torrents/${active.id}/data`);
      const data = res.data;
      const link = document.createElement('a');
      link.download = `${active.name}.torrent`;
      link.href = `data:application/x-bittorrent;base64,${data}`;
      link.click();
    } catch (e: any) {
      dispatch(setToastMsg(e.message));
    }
    dispatch(toggleTorrentPending(active.id, false));
  }

  return (
    <Fragment>
      <Editable
        pending={active.pending}
        className={classes.title}
        value={active.title}
        required
        onSubmit={title => handleUpdateTorrent({ title })}
      />

      <div className={classes.tabContent}>
        <Grid container spacing={2}>
          <Grid item xs={labelWidth} className={classes.rowLabel}>
            Id
          </Grid>
          <Grid item xs={contentWidth}>
            {active.id}
          </Grid>
          <Grid item xs={labelWidth} className={classes.rowLabel}>
            Name
          </Grid>
          <Grid item xs={contentWidth}>
            {active.name}
          </Grid>
          <Grid item xs={labelWidth} className={classes.rowLabel}>
            Season
          </Grid>
          <Grid item xs={contentWidth}>
            <Editable
              pending={active.pending}
              value={`${active.season || ""}`}
              size="small"
              onSubmit={season =>
                handleUpdateTorrent({
                  season: season ? parseInt(season) : null
                })
              }
            />
          </Grid>
          <Grid item xs={labelWidth} className={classes.rowLabel}>
            Episode
          </Grid>
          <Grid item xs={contentWidth}>
            <Editable
              pending={active.pending}
              value={`${active.episode || ""}`}
              size="small"
              onSubmit={episode =>
                handleUpdateTorrent({
                  episode: episode ? parseInt(episode) : null
                })
              }
            />
          </Grid>
          <Grid item xs={labelWidth} className={classes.rowLabel}>
            Status
          </Grid>
          <Grid item xs={contentWidth}>
            {active.status}
            {active.state && `:${active.state}`}
          </Grid>
          <Grid item xs={labelWidth} className={classes.rowLabel}>
            Save folder
          </Grid>
          <Grid item xs={contentWidth}>
            {active.folder}
          </Grid>
          <Grid item xs={labelWidth} className={classes.rowLabel}>
            Size
          </Grid>
          <Grid item xs={contentWidth}>
            {active.size}
          </Grid>
          <Grid item xs={labelWidth} className={classes.rowLabel}>
            Hash
          </Grid>
          <Grid item xs={contentWidth}>
            {active.hash}
          </Grid>
          <Grid item xs={labelWidth} className={classes.rowLabel}>
            Created At
          </Grid>
          <Grid item xs={contentWidth}>
            {active.createdAt}
          </Grid>
          <Grid item xs={labelWidth} className={classes.rowLabel}>
            Error
          </Grid>
          <Grid item xs={contentWidth} className={classes.error}>
            {active.message}
            {active.downloadError}
          </Grid>
        </Grid>
      </div>

      <Typography variant="subtitle2">Subtitles</Typography>
      <div className={classes.tabContent}>
        <Grid container spacing={2}>
          {active.subtitles &&
            active.subtitles.length > 0 &&
            active.subtitles.map(subtitle => (
              <Grid key={subtitle} item xs={12}>
                <Editable
                  pending={active.pending}
                  value={subtitle}
                  size="small"
                  required
                  onSubmit={newSubtitle =>
                    handleRenameSubtitle(subtitle, newSubtitle)
                  }
                  onDelete={() => handleDelSubtitle(subtitle)}
                />
              </Grid>
            ))}
          {(!active.subtitles || active.subtitles.length === 0) && (
            <Grid item xs={12}>
              no subtitle
            </Grid>
          )}
        </Grid>
      </div>

      <div className={classes.grow} />
      <div className={classes.buttons}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={pushDownload}
          disabled={active.pending}
        >
          Push To Download
        </Button>
        <FileButton
          size="small"
          variant="contained"
          color="primary"
          onPick={handleAddSubtitle}
          disabled={active.pending}
        >
          Add Subtitle
        </FileButton>
        <Button size="small" variant="contained" color="default" onClick={handleExport} >
          Export Torrent
        </Button>
        {active.status === TorrentStatus.PUSHED && active.state === 'PAUSED' &&
          <Button size="small" variant="contained" color="default" onClick={handleResume}>
            Resume
        </Button>
        }
        {active.status === TorrentStatus.PUSHED && active.state !== 'PAUSED' &&
          <Button size="small" variant="contained" color="default" onClick={handlePause}>
            Pause
        </Button>
        }
      </div>
    </Fragment>
  );
}
